import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import qs from "qs";
import { RoutesEnum, SocketActionEnum, StatusStateEnum } from "~/types/enums";
import logo from "~/assets/i/logo.svg";
import { Box, Button, Card, Icon, Link, Modal, Spinner, Text } from "~/components-shared";
import Footer from "~/components/Footer";
import CompanyIntegrations from "~/components/CompanyIntegrations";
import rules from "~/lib/rules";
import { AbacProvider } from "react-abac";
import integrationsActionCreators from "~/store/integrations/actions";
import { useDispatch, useSelector } from "react-redux";
import integrationSelectors from "~/store/integrations/selectors";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { useTranslation } from "react-i18next";
import StyledIcon from "~/components/StyledIcon";
import ShareIntegrationsModal from "~/components/ShareIntegrationsModal";
import { io } from "socket.io-client";
import globalActionCreators from "~/store/global/actions";
const Section = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-size: contain;
`;
const Branding = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 10;
}}px;
  margin-bottom: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing.step * 5;
}}px;
`;
const Logo = styled.img`
  margin-right: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.spacing.step;
}}px;
`;
const SOCKET_API = process.env.WEBSOCKET_URL || "";
const PublicIntegrations = () => {
  const {
    t
  } = useTranslation();
  const {
    search
  } = useLocation();
  const {
    token,
    integrations
  } = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const history = useHistory();
  const integrationStatus = useSelector(integrationSelectors.getStatus);
  const dispatch = useDispatch();
  const [user, setUser] = useState(undefined);
  const integrationArray = integrations?.split(",") || [];
  const [shareIntegrations, setShareIntegrations] = useState(false);
  const [info, setInfo] = useState(false);
  useMemo(() => {
    if (!token) {
      history.replace(RoutesEnum.ROOT);
    }
    try {
      setUser(jwtDecode(token));
    } catch (e) {
      history.replace(RoutesEnum.ROOT);
    }
  }, [token]);
  useEffect(() => {
    if (user && integrationStatus === StatusStateEnum.IDLE) {
      const socket = io(SOCKET_API, {
        auth: {
          token
        },
        query: {
          memberId: user.id,
          companyId: user.company.id
        }
      });
      socket.on(SocketActionEnum.CREATE_INTEGRATIONS, () => {
        dispatch(globalActionCreators.change("integrations.status.all", StatusStateEnum.IDLE));
      });
      socket.on(SocketActionEnum.CREATE_INTEGRATION, () => {
        dispatch(globalActionCreators.change("integrations.status.all", StatusStateEnum.IDLE));
      });
      socket.on(SocketActionEnum.CREATE_MEMBER_INTEGRATION, _ref4 => {
        let {
          memberId
        } = _ref4;
        if (memberId === user.id) {
          dispatch(globalActionCreators.change("integrations.status.member", StatusStateEnum.IDLE));
        }
      });
      socket.on(SocketActionEnum.UPDATE_INTEGRATION, () => {
        dispatch(globalActionCreators.change("integrations.status.all", StatusStateEnum.IDLE));
        dispatch(globalActionCreators.change("integrations.status.member", StatusStateEnum.IDLE));
      });
      socket.on(SocketActionEnum.DELETE_INTEGRATION, () => {
        dispatch(globalActionCreators.change("integrations.status.all", StatusStateEnum.IDLE));
        dispatch(globalActionCreators.change("integrations.status.member", StatusStateEnum.IDLE));
      });
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      dispatch(integrationsActionCreators.fetchStarted());
      dispatch(integrationsActionCreators.fetchMemberStarted());
    }
  }, [integrationStatus, user]);
  if (!user) {
    return /*#__PURE__*/React.createElement(Section, {
      style: {
        justifyContent: "center",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement(Spinner, null));
  }
  if (user.exp < Math.floor(Date.now() / 1000)) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(AbacProvider, {
      user: user,
      roles: [user.role.id],
      rules: rules,
      permissions: user.authorities
    }, /*#__PURE__*/React.createElement(Branding, null, /*#__PURE__*/React.createElement(Logo, {
      src: logo,
      alt: "Joyn",
      width: 40
    }), /*#__PURE__*/React.createElement("h2", null, "Joyn")), /*#__PURE__*/React.createElement(Box, {
      fullWidth: true,
      direction: "column",
      justify: "center",
      style: {
        flexGrow: 1
      }
    }, /*#__PURE__*/React.createElement(Box, {
      direction: "column",
      align: "center",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "warningOutline",
      color: "translucentDanger",
      s: {
        mb: 5
      }
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      fontSize: 10,
      align: "center",
      s: {
        mb: 3
      }
    }, t("VerifyEmail.oops"), "!"), /*#__PURE__*/React.createElement(Text, {
      element: "h2",
      fontSize: 7,
      align: "center",
      s: {
        mb: 3
      }
    }, "Seems token has expired"))), /*#__PURE__*/React.createElement(Footer, null)));
  }
  const handleMeetJoyn = () => {
    window.open("https://calendly.com/kendra-joyn/30min", "_blank");
  };
  const handleShare = () => {
    setShareIntegrations(true);
  };
  const toggleInfo = () => {
    setInfo(prev => !prev);
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(AbacProvider, {
    user: user,
    roles: [user.role.id],
    rules: rules,
    permissions: user.authorities
  }, /*#__PURE__*/React.createElement(Branding, null, /*#__PURE__*/React.createElement(Logo, {
    src: logo,
    alt: "Joyn",
    width: 40
  }), /*#__PURE__*/React.createElement("h2", null, "Joyn")), /*#__PURE__*/React.createElement(Box, {
    fullWidth: true,
    direction: "column",
    align: "center",
    style: {
      flexGrow: 1
    }
  }, /*#__PURE__*/React.createElement(Card, {
    maxWidth: "700px",
    s: {
      px: 10,
      pb: 10
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    align: "center",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    s: {
      mb: 5
    }
  }, t("PublicIntegrations.manageIntegrations", {
    name: user.company.name
  })), /*#__PURE__*/React.createElement(Box, {
    gap: 5,
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(Button, {
    color: "translucent",
    onClick: handleMeetJoyn,
    fontSize: 3.5,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "eventAvailable",
      s: {
        mr: 2
      }
    }),
    s: {
      py: 2
    }
  }, t("PublicIntegrations.meetWithJoyn")), /*#__PURE__*/React.createElement(Button, {
    color: "translucent",
    onClick: handleShare,
    fontSize: 3.5,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      s: {
        mr: 2
      }
    }),
    s: {
      py: 2
    }
  }, t("PublicIntegrations.shareWithAdmin"))), /*#__PURE__*/React.createElement(Button, {
    color: info ? "translucentPrimary" : "transparent",
    fontSize: 3,
    onClick: toggleInfo,
    s: {
      px: 2,
      py: 1,
      mb: 5
    },
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "infoOutline",
      color: "primary",
      s: {
        mr: 1
      }
    }),
    elemAfter: /*#__PURE__*/React.createElement(Icon, {
      src: "chevron",
      color: "inherit",
      rotate: info ? 0 : 180,
      s: {
        ml: 1
      }
    }),
    outline: false
  }, t("PublicIntegrations.whatToExpect")), info && /*#__PURE__*/React.createElement(Text, {
    align: "center",
    color: "textSecondary",
    fontSize: 3,
    display: "block",
    s: {
      mb: 5
    }
  }, t("PublicIntegrations.whatToExpectInfo"), /*#__PURE__*/React.createElement(Link, {
    color: "inherit",
    href: "mailto:support@joyn.one",
    underline: true
  }, "support@joyn.one")), /*#__PURE__*/React.createElement(CompanyIntegrations, {
    filter: integrationArray,
    fullWidth: true,
    showSoc2Logo: true
  })))), /*#__PURE__*/React.createElement(Modal, {
    open: shareIntegrations
  }, /*#__PURE__*/React.createElement(ShareIntegrationsModal, {
    filterIntegrations: integrationArray,
    onClose: () => setShareIntegrations(false)
  })), /*#__PURE__*/React.createElement(Footer, null)));
};
export default PublicIntegrations;